import { useTranslation } from "react-i18next";
import {
    LifeBuoy01,
    Globe02,
    FileCheck02,
    File02,
    Route,
    Mail01,
    Check,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { EveLogo } from "@/components/foundations/logo/eve-logo";
import { LanguageSelector } from "@/components/application/language-selector/language-selector";
import { VersionFooter } from "@/components/base/version-footer/version-footer";

export const LandingPage = () => {
    const { t } = useTranslation("pages:landing");

    const agents = [
        {
            key: "care",
            icon: LifeBuoy01,
            gradient: "from-blue-500/20 to-cyan-500/20",
            iconColor: "text-blue-400",
            imageUrl: "https://images.unsplash.com/photo-1553775282-20af80779df7?w=800&h=600&fit=crop&q=80",
            imageAlt: "Customer service team",
        },
        {
            key: "track",
            icon: Globe02,
            gradient: "from-green-500/20 to-emerald-500/20",
            iconColor: "text-green-400",
            imageUrl: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop&q=80",
            imageAlt: "Logistics tracking and shipping",
        },
        {
            key: "quote",
            icon: FileCheck02,
            gradient: "from-purple-500/20 to-pink-500/20",
            iconColor: "text-purple-400",
            imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop&q=80",
            imageAlt: "Business documents and quotations",
        },
        {
            key: "read",
            icon: File02,
            gradient: "from-orange-500/20 to-red-500/20",
            iconColor: "text-orange-400",
            imageUrl: "https://images.unsplash.com/photo-1516637090014-cb1ab78511f5?w=800&h=600&fit=crop&q=80",
            imageAlt: "Document reading and data extraction",
        },
        {
            key: "plan",
            icon: Route,
            gradient: "from-indigo-500/20 to-blue-500/20",
            iconColor: "text-indigo-400",
            imageUrl: "https://images.unsplash.com/photo-1593931769132-a3fa26120f1e?w=800&h=600&fit=crop&q=80",
            imageAlt: "Route planning and logistics",
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    return (
        <div className={`min-h-screen bg-secondary relative ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Unified Animated Blob Backgrounds - covers entire page */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-brand-500/20 blur-3xl animate-blob" />
                <div className="absolute top-1/2 -left-40 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl animate-blob animation-delay-2000" />
                <div className="absolute -bottom-40 right-1/4 h-80 w-80 rounded-full bg-purple-500/20 blur-3xl animate-blob animation-delay-4000" />
                <div className="absolute top-1/4 left-1/3 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl animate-blob animation-delay-6000" />
                <div className="absolute top-3/4 right-1/3 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl animate-blob animation-delay-3000" />
            </div>

            {/* Header */}
            <header className="relative z-20 border-b border-secondary_alt bg-primary/80 backdrop-blur-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-14 sm:h-16 items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <EveLogo className="h-6 w-6 sm:h-8 sm:w-8" />
                            <h3 className="text-lg sm:text-2xl font-bold text-primary">Eve Mind</h3>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                            <Button
                                href="/auth/login"
                                size="sm"
                                color="primary"
                                className="sm:!size-md"
                            >
                                {t("hero.ctaLogin")}
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative z-10 py-12 sm:py-20 lg:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <div className="flex justify-center mb-6 sm:mb-8">
                            <EveLogo className="h-16 w-16 sm:h-24 sm:w-24 lg:h-32 lg:w-32" />
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-primary sm:text-5xl lg:text-6xl xl:text-7xl">
                            {t("hero.title")}
                        </h1>
                        <p className="mt-4 text-lg font-semibold text-tertiary sm:text-xl lg:text-2xl">
                            {t("hero.claim")}
                        </p>
                        <p className="mt-3 text-base text-quaternary sm:text-lg">
                            {t("hero.description")}
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative z-10 py-12 sm:py-16 lg:py-24">
                <div className="relative w-full">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-8 sm:py-12 lg:py-16">
                            {/* Left side - Text content */}
                            <div className="text-primary space-y-4 sm:space-y-6 relative z-10 order-2 lg:order-1">
                                <div>
                                    <p className="text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl xl:text-5xl">
                                        {t("cta.headline")}
                                    </p>
                                    <p className="mt-3 sm:mt-4 text-lg font-semibold sm:text-xl lg:text-2xl text-brand-secondary">
                                        {t("cta.subline")}
                                    </p>
                                </div>
                            </div>

                            {/* Right side - Image collage */}
                            <div className="relative h-[300px] sm:h-[400px] lg:h-[500px] xl:h-[600px] z-10 order-1 lg:order-2">
                                <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-2">
                                    {/* Top right - Container ship */}
                                    <div className="col-start-2 col-span-2 row-span-2 rounded-lg overflow-hidden bg-secondary/20 backdrop-blur-sm border border-white/10 relative group">
                                        <img
                                            src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=800&h=600&fit=crop&q=80"
                                            alt="Container ship"
                                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 via-transparent to-transparent" />
                                    </div>

                                    {/* Middle left - Cargo truck */}
                                    <div className="col-span-1 row-start-2 row-span-2 rounded-lg overflow-hidden bg-secondary/20 backdrop-blur-sm border border-white/10 relative group">
                                        <img
                                            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=800&fit=crop&q=80"
                                            alt="Cargo truck"
                                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 via-transparent to-transparent" />
                                    </div>

                                    {/* Bottom middle - Port terminal */}
                                    <div className="col-start-2 col-span-1 row-start-3 row-span-1 rounded-lg overflow-hidden bg-secondary/20 backdrop-blur-sm border border-white/10 relative group">
                                        <img
                                            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop&q=80"
                                            alt="Port terminal"
                                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 via-transparent to-transparent" />
                                    </div>

                                    {/* Bottom right - Warehouse */}
                                    <div className="col-start-3 col-span-1 row-start-3 row-span-1 rounded-lg overflow-hidden bg-secondary/20 backdrop-blur-sm border border-white/10 relative group">
                                        <img
                                            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop&q=80"
                                            alt="Warehouse"
                                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 via-transparent to-transparent" />
                                    </div>

                                    {/* Top left - Air cargo */}
                                    <div className="col-span-1 row-span-1 rounded-lg overflow-hidden bg-secondary/20 backdrop-blur-sm border border-white/10 relative group">
                                        <img
                                            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=400&fit=crop&q=80"
                                            alt="Air cargo"
                                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 via-transparent to-transparent" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="relative z-10 py-12 sm:py-20 lg:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mx-auto max-w-2xl text-center mb-12 sm:mb-16 lg:mb-20">
                        <p className="text-sm font-semibold uppercase tracking-wider text-brand-600 mb-4">
                            {t("features.label")}
                        </p>
                        <h2 className="text-3xl font-bold text-primary sm:text-4xl lg:text-5xl">
                            {t("features.title")}
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-quaternary">
                            {t("features.description")}
                        </p>
                    </div>

                    {/* Agent Features */}
                    <div className="space-y-16 sm:space-y-20 lg:space-y-24">
                        {agents.map((agent, index) => {
                            const Icon = agent.icon;
                            const features = t(`agents.${agent.key}.features`, { returnObjects: true }) as string[];
                            const isEven = index % 2 === 0;

                            return (
                                <div
                                    key={agent.key}
                                    className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 sm:gap-12 lg:gap-16 items-center`}
                                >
                                    {/* Text Content */}
                                    <div className="flex-1 w-full lg:w-auto">
                                        <div className="inline-flex items-center justify-center rounded-xl bg-secondary p-3 ring-1 ring-secondary_alt mb-6">
                                            <Icon className={`h-6 w-6 ${agent.iconColor}`} />
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl font-bold text-primary mb-4">
                                            {t(`agents.${agent.key}.title`)}
                                        </h3>
                                        <p className="text-base sm:text-lg text-quaternary mb-6 leading-relaxed">
                                            {t(`agents.${agent.key}.description`)}
                                        </p>
                                        <ul className="space-y-3">
                                            {features.map((feature, featureIndex) => (
                                                <li key={featureIndex} className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        <Check className="h-5 w-5 text-brand-600" />
                                                    </div>
                                                    <span className="text-sm sm:text-base text-quaternary">
                                                        {feature}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Image */}
                                    <div className="flex-1 w-full lg:w-auto">
                                        <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-secondary_alt group">
                                            <img
                                                src={agent.imageUrl}
                                                alt={agent.imageAlt}
                                                className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 via-transparent to-transparent" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative z-10 border-t border-secondary_alt bg-primary/95 backdrop-blur-sm py-12 sm:py-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-xl font-bold text-primary sm:text-2xl lg:text-3xl px-4 sm:px-0">
                            {t("footer.contactTitle")}
                        </h2>
                        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-quaternary px-4 sm:px-0">
                            {t("footer.contactDescription")}
                        </p>
                        <div className="mt-6 sm:mt-8 flex flex-col items-stretch sm:items-center justify-center gap-3 sm:gap-4 px-4 sm:px-0">
                            <Button
                                href="mailto:eve.mind.ai@gmail.com"
                                size="lg"
                                color="primary"
                                iconLeading={Mail01}
                                className="w-full sm:w-auto"
                            >
                                {t("footer.contactButton")}
                            </Button>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs sm:text-sm text-quaternary">
                                <span>{t("footer.loginLink")}</span>
                                <Button
                                    href="/auth/login"
                                    size="sm"
                                    color="link-color"
                                >
                                    {t("footer.loginButton")}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 sm:mt-12 border-t border-secondary_alt pt-6 sm:pt-8">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex justify-center sm:justify-start">
                                <LanguageSelector showLabel={false} />
                            </div>
                            <p className="text-xs sm:text-sm text-quaternary text-center sm:text-right">
                                © {new Date().getFullYear()} Eve Mind. All rights reserved.
                            </p>
                        </div>
                        <div className="mt-4">
                            <VersionFooter />
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

